import { html, type TemplateResult } from 'lit-html';
import { ApiError, get } from '../api.js';
import { act, app, closeModal, copy, emptyRow, openModal, type Page } from '../app.js';
import { pill, when } from '../format.js';
import type { AuditEntry, Connector, Exchange } from '../types.js';

let entries: AuditEntry[] = [];
let connectors: Pick<Connector, 'name' | 'display_name'>[] = [];
const filters = { day: '', connector: '', actor: '', direction: '' };

async function load(): Promise<void> {
  if (!filters.day) filters.day = new Date().toISOString().slice(0, 10);
  const q = new URLSearchParams(Object.entries({ day: filters.day, connector: filters.connector, actor: filters.actor }).filter(([, v]) => v));
  const [r, c] = await Promise.all([
    get<{ day: string; entries: AuditEntry[] }>(`/activity?${q}`),
    connectors.length ? null : get<{ connectors: Connector[] }>('/connectors'),
  ]);
  entries = r.entries;
  if (c) connectors = c.connectors.map(({ name, display_name }) => ({ name, display_name }));
}

const tone = (s: number) => (s === 0 || s >= 500 ? 'critical' : s >= 400 ? 'warning' : 'positive');
const timeOf = (iso: string) => new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', second: '2-digit' });
const connectorName = (n?: string) => connectors.find((c) => c.name === n)?.display_name ?? n ?? '';

function whoText(e: AuditEntry): string {
  if (e.direction === 'out') return e.actor.startsWith('job:') ? `Background job (${e.actor.slice(4)})` : 'This service';
  if (e.actor_type === 'api_key') return e.actor_name ? `Key “${e.actor_name}”` : e.actor;
  if (e.actor_type === 'webhook') return `${connectorName(e.connector)} webhook`;
  return e.actor;
}

function detailText(e: AuditEntry): string {
  if (!e.detail) return '';
  return Object.entries(e.detail)
    .map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : String(v)}`)
    .join(' · ');
}

/** JSON pretty-printed; form bodies (Stannp requests) one decoded field per line; anything else as-is. */
function pretty(body: string | null, contentType = ''): string {
  if (!body) return '';
  try {
    return JSON.stringify(JSON.parse(body), null, 2);
  } catch {
    /* not JSON */
  }
  if (contentType.includes('x-www-form-urlencoded') || /^[\w%[\]]+=[^&]*(&[\w%[\]]+=[^&]*)*$/.test(body)) {
    return [...new URLSearchParams(body)].map(([k, v]) => `${k} = ${v}`).join('\n');
  }
  return body;
}

// ---- Details dialog -------------------------------------------------------------------------
interface Details {
  exchange: Exchange;
  children: Exchange[];
  parent: { id: string; method: string; url: string; status: number | null; at: string } | null;
}
let details: { entry: AuditEntry | null; data: Details | null; error: string | null } = { entry: null, data: null, error: null };

async function showExchange(id: string, entry: AuditEntry | null): Promise<void> {
  details = { entry, data: null, error: null };
  openModal(detailsView);
  try {
    details.data = await get<Details>(`/activity/exchanges/${encodeURIComponent(id)}`);
  } catch (err) {
    details.error = (err as ApiError).message;
  }
  app.rerender();
}

function openEntry(e: AuditEntry): void {
  if (e.exchange_id) {
    void showExchange(e.exchange_id, e);
    return;
  }
  details = { entry: e, data: null, error: null };
  openModal(detailsView);
}

function headersTable(h: Record<string, string>): TemplateResult {
  const rows = Object.entries(h);
  if (!rows.length) return html`<p class="muted">No headers.</p>`;
  return html`<dl class="kv">${rows.map(([k, v]) => html`<dt>${k}</dt><dd><code class="id">${v}</code></dd>`)}</dl>`;
}

function part(title: string, p: { headers: Record<string, string>; body: string | null; truncated?: boolean }, line: TemplateResult): TemplateResult {
  const body = pretty(p.body, p.headers['content-type']);
  return html`<section class="mt-5">
    <h3>${title}</h3>
    <p>${line}</p>
    <details class="mt-4"><summary class="muted">Headers (${Object.keys(p.headers).length})</summary><div class="mt-4">${headersTable(p.headers)}</div></details>
    ${body
      ? html`<pre class="code">${body}</pre>
          <div class="actions-row no-margin">${p.truncated ? html`<span class="muted">Cut at 64 KB.</span>` : ''}
            <button class="btn quiet compact" @click=${() => copy(body, 'Body copied')}>Copy body</button></div>`
      : html`<p class="muted">No body.</p>`}
  </section>`;
}

function detailsView(): TemplateResult {
  const { entry: e, data, error } = details;
  const x = data?.exchange;
  return html`<div class="modal-backdrop" @click=${(ev: Event) => ev.target === ev.currentTarget && closeModal()}>
    <div class="modal wide" role="dialog" aria-modal="true" aria-labelledby="activity-title">
      <div class="row-between"><h2 id="activity-title">${x ? `${x.method} ${x.direction === 'out' ? new URL(x.url).host + new URL(x.url).pathname : x.url.split('?')[0]}` : e ? `${e.method} ${e.route}` : 'Call'}</h2>
        <button class="btn quiet compact" @click=${closeModal}>Close</button></div>
      ${e
        ? html`<dl class="kv mt-4">
            <dt>When</dt><dd>${when(e.ts)}</dd>
            <dt>Direction</dt><dd>${e.direction === 'out' ? 'Outgoing: this service called out' : 'Incoming: a call to this service'}</dd>
            <dt>Who</dt><dd>${whoText(e)}${e.ip ? html` <span class="muted">from ${e.ip}</span>` : ''}</dd>
            ${e.connector ? html`<dt>Connector</dt><dd>${connectorName(e.connector)}</dd>` : ''}
            <dt>Result</dt><dd>${pill(e.status ? String(e.status) : 'no answer', tone(e.status))} <span class="muted">${e.latency_ms} ms</span></dd>
            ${e.action ? html`<dt>Change</dt><dd><b>${e.action}</b>${e.detail ? html`<br><span class="muted">${detailText(e)}</span>` : ''}</dd>` : ''}
            <dt>Request ID</dt><dd><code class="id">${e.request_id}</code></dd>
          </dl>`
        : ''}
      ${error ? html`<div class="banner warning mt-4">${error}</div>` : ''}
      ${!e?.exchange_id && !x && !error
        ? html`<p class="hint mt-4">${e?.actor_type === 'admin' ? 'Changes made in this console keep only the summary above: their bodies can contain credentials.' : 'No request details were recorded for this row.'}</p>`
        : ''}
      ${e?.exchange_id && !data && !error ? html`<div class="loading mt-4"><span class="spinner" aria-hidden="true"></span>Loading details…</div>` : ''}
      ${x
        ? html`
            ${data!.parent
              ? html`<p class="mt-4">Made while handling <a href="#" @click=${(ev: Event) => { ev.preventDefault(); void showExchange(data!.parent!.id, null); }}>${data!.parent.method} ${new URL(data!.parent.url, 'https://x').pathname}</a>.</p>`
              : ''}
            ${part('Request', x.request, html`<code>${x.method} ${x.url}</code>`)}
            ${x.response
              ? part('Response', x.response, html`${pill(String(x.response.status), tone(x.response.status))} <span class="muted">${x.duration_ms} ms</span>`)
              : html`<section class="mt-5"><h3>Response</h3><div class="banner critical">No answer: ${x.error ?? 'unknown error'}</div></section>`}
            ${data!.children.length
              ? html`<section class="mt-5"><h3>Calls it made</h3>
                  <div class="table-wrap"><table class="clickable">
                    <thead><tr><th>To</th><th>Result</th><th class="num">ms</th></tr></thead>
                    <tbody>${data!.children.map((c) => html`<tr @click=${() => showExchange(c.id, null)}>
                      <td><code class="id">${c.method} ${new URL(c.url).host}${new URL(c.url).pathname}</code></td>
                      <td>${c.response ? pill(String(c.response.status), tone(c.response.status)) : pill('no answer', 'critical')}</td>
                      <td class="num">${c.duration_ms}</td></tr>`)}</tbody>
                  </table></div></section>`
              : ''}
            <p class="hint mt-4">Credentials are never stored: API keys show only their preview, other auth headers show as [hidden]. Details are kept 30 days.</p>`
        : ''}
    </div></div>`;
}

// ---- Page ---------------------------------------------------------------------------------
export const activityPage: Page = {
  title: 'Activity',
  load,
  view() {
    const apply = (e: Event) => {
      e.preventDefault();
      const v = Object.fromEntries(new FormData(e.target as HTMLFormElement)) as Record<string, string>;
      Object.assign(filters, { day: v.day ?? '', connector: v.connector ?? '', actor: (v.actor ?? '').trim(), direction: v.direction ?? '' });
      void act(load, null);
    };
    const shown = entries.filter((e) => !filters.direction || (e.direction ?? 'in') === filters.direction);
    return html`
      <h1>Activity</h1>
      <p class="hint">Every connector call, webhook delivery, outgoing call to a vendor or destination, and change made here, by UTC day.
        Click a row for the full request and response (kept 30 days). Rows are kept 400 days.</p>
      <form class="filters" @submit=${apply}>
        <label>Day (UTC)<input type="date" name="day" .value=${filters.day}></label>
        <label>Connector<select name="connector"><option value="">All</option>${connectors.map((c) => html`<option value=${c.name} ?selected=${filters.connector === c.name}>${c.display_name}</option>`)}</select></label>
        <label>Direction<select name="direction">${[['', 'In and out'], ['in', 'Incoming'], ['out', 'Outgoing']].map(([v, l]) => html`<option value=${v!} ?selected=${filters.direction === v}>${l}</option>`)}</select></label>
        <label>Who (exact)<input name="actor" placeholder="email, or key:ID" .value=${filters.actor}></label>
        <button class="btn">Show</button>
      </form>
      <div class="table-wrap"><table class="clickable">
        <thead><tr><th>Time</th><th></th><th>Who</th><th>What</th><th>Result</th><th class="num">ms</th></tr></thead>
        <tbody>${shown.length
          ? shown.map(
              (e) => html`<tr @click=${() => openEntry(e)}>
                <td class="nowrap">${timeOf(e.ts)}</td>
                <td>${e.direction === 'out' ? pill('Out', 'info') : pill('In')}</td>
                <td>${whoText(e)}${e.ip ? html`<br><span class="muted">${e.ip}</span>` : ''}</td>
                <td>${e.action ? html`<b>${e.action}</b><br>` : ''}<code class="id">${e.method} ${e.route}</code>${e.detail ? html`<br><span class="muted">${detailText(e)}</span>` : ''}</td>
                <td>${pill(e.status ? String(e.status) : 'no answer', tone(e.status))}</td>
                <td class="num">${e.latency_ms}</td>
              </tr>`,
            )
          : emptyRow(6, `Nothing recorded on ${filters.day}.`)}</tbody>
      </table></div>
      ${entries.length >= 500 ? html`<p class="hint">Showing the latest 500. Narrow the filters to see more.</p>` : ''}`;
  },
};
